import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import theme, { conditionBg } from '../theme';
import { patchException } from '../../services/clientReportsApi';

const COLORS = theme.colors;

const TYPE_LABEL = {
  partial_cycle: 'Partial cycle',
  overdue_room: 'Overdue room',
  residual_positive: 'Residual positive',
  motion_abort: 'Motion abort',
  manual_wipe_required: 'Manual wipe required',
  incomplete_record: 'Incomplete record (no manual clean)',
};

function normalizeStatus(status) {
  return String(status || '')
    .trim()
    .toLowerCase();
}

function exceptionId(ex) {
  return ex?.id || ex?._id || '';
}

/**
 * @param {{
 *   exceptions: object[],
 *   loading?: boolean,
 *   error?: string,
 *   email: string,
 *   onChanged?: () => void,
 *   onOpenPack?: (historyId: string) => void,
 *   readOnly?: boolean,
 * }} props
 */
export default function ExceptionQueuePanel({
  exceptions = [],
  loading,
  error,
  email,
  onChanged,
  onOpenPack,
  readOnly = false,
}) {
  const [filter, setFilter] = useState('open');
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [busyId, setBusyId] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  /** Local overrides so Acknowledge/Resolve show immediately */
  const [statusOverrides, setStatusOverrides] = useState({});

  const rows = useMemo(
    () =>
      (exceptions || []).map((ex) => {
        const id = exceptionId(ex);
        const override = id ? statusOverrides[id] : null;
        return override ? { ...ex, status: override } : ex;
      }),
    [exceptions, statusOverrides]
  );

  const counts = useMemo(() => {
    const c = { open: 0, acknowledged: 0, resolved: 0, all: rows.length };
    rows.forEach((ex) => {
      const s = normalizeStatus(ex.status);
      if (s === 'open') c.open += 1;
      else if (s === 'acknowledged') c.acknowledged += 1;
      else if (s === 'resolved') c.resolved += 1;
    });
    return c;
  }, [rows]);

  const filtered = rows.filter((ex) => {
    const s = normalizeStatus(ex.status);
    if (filter === 'all') return true;
    if (filter === 'open') return s === 'open' || s === 'acknowledged';
    return s === filter;
  });

  const emptyMessage = () => {
    if (filter === 'acknowledged') {
      return counts.acknowledged === 0
        ? 'No acknowledged exceptions yet. Open an item with status “open” and click Acknowledge first.'
        : 'No exceptions in this view.';
    }
    if (filter === 'resolved') {
      return 'No resolved exceptions yet. Use Resolve on an open item to close it.';
    }
    if (filter === 'open') {
      return 'No open or acknowledged exceptions.';
    }
    return 'No exceptions in this view.';
  };

  const patchStatus = async (ex, status, note) => {
    const id = exceptionId(ex);
    if (!id) {
      setActionError('Cannot update this exception — missing id from the server.');
      return;
    }
    if (!email) {
      setActionError('Cannot update exception — missing account email.');
      return;
    }

    setBusyId(id);
    setActionError('');
    setActionSuccess('');
    try {
      await patchException(id, email, { status, resolutionNote: note || '' });
      setStatusOverrides((prev) => ({ ...prev, [id]: status }));
      setActionSuccess(
        status === 'acknowledged'
          ? 'Exception acknowledged. Switch the filter to “Acknowledged” to see only these.'
          : status === 'resolved'
            ? 'Exception marked resolved. It no longer counts in Open exceptions.'
            : 'Exception updated.'
      );
      if (status === 'acknowledged') {
        setFilter('acknowledged');
      } else if (status === 'resolved') {
        setFilter('resolved');
      }
      onChanged?.();
    } catch (e) {
      console.error('patch exception', e);
      setActionError(
        e?.response?.data?.message ||
          e?.message ||
          'Failed to update exception. Check your connection and try again.'
      );
    } finally {
      setBusyId('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${COLORS.borderDefault}`,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: expanded ? 2 : 0,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>
            Exception queue
          </Typography>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5 }}>
            Disinfection and environmental-hygiene gaps on Completed cycles only: overdue rooms,
            motion aborts, post-UVC residuals, manual wipe gaps, and incomplete records (UV without
            manual clean). Partial cycles are excluded. Not a hospital-wide CAPA, policy, or
            quality-management workflow.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {expanded ? (
            <TextField
              select
              size="small"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setActionSuccess('');
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="open">
                Open ({counts.open + counts.acknowledged})
              </MenuItem>
              <MenuItem value="acknowledged">Acknowledged ({counts.acknowledged})</MenuItem>
              <MenuItem value="resolved">Resolved ({counts.resolved})</MenuItem>
              <MenuItem value="all">All ({counts.all})</MenuItem>
            </TextField>
          ) : null}
          <Button
            size="small"
            variant="contained"
            endIcon={expanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
            onClick={() => setExpanded((v) => !v)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: `${theme.radius.md}px`,
              flexShrink: 0,
              background: theme.gradients.primary,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: theme.gradients.primaryHover,
                boxShadow: 'none',
              },
            }}
          >
            {expanded ? 'Close' : 'Open'}
          </Button>
        </Box>
      </Box>

      {expanded ? (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          {actionError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          ) : null}
          {actionSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess('')}>
              {actionSuccess}
            </Alert>
          ) : null}
          {readOnly ? (
            <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
              View only — Acknowledge and Resolve are disabled for your role or this support view.
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, py: 2 }}>
              {emptyMessage()}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {filtered.map((ex) => {
                const id = exceptionId(ex);
                const status = normalizeStatus(ex.status);
                const busy = busyId === id;
                return (
                  <Box
                    key={id || ex.title}
                    sx={{
                      p: 1.75,
                      borderRadius: `${theme.radius.md}px`,
                      border: `1px solid ${COLORS.borderDefault}`,
                      bgcolor: conditionBg(ex.severity === 'high' ? 'danger' : 'warning'),
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
                        {ex.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Chip size="small" label={TYPE_LABEL[ex.type] || ex.type} />
                        <Chip
                          size="small"
                          label={status || '—'}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 0.75 }}>
                      {ex.detail}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mt: 0.5 }}>
                      {ex.occurredAt ? new Date(ex.occurredAt).toLocaleString('en-GB') : '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                      {ex.historyId ? (
                        <Button
                          size="small"
                          sx={{ textTransform: 'none' }}
                          onClick={() => onOpenPack?.(ex.historyId)}
                        >
                          Open pack
                        </Button>
                      ) : null}
                      {status === 'open' && !readOnly ? (
                        <Button
                          size="small"
                          disabled={busy || !id}
                          sx={{ textTransform: 'none' }}
                          onClick={() => patchStatus(ex, 'acknowledged')}
                        >
                          {busy ? 'Updating…' : 'Acknowledge'}
                        </Button>
                      ) : null}
                      {status !== 'resolved' && !readOnly ? (
                        <Button
                          size="small"
                          disabled={busy || !id}
                          sx={{ textTransform: 'none' }}
                          onClick={() => {
                            setResolveTarget(ex);
                            setResolutionNote('');
                            setActionError('');
                          }}
                        >
                          Resolve
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      ) : null}

      <Dialog open={Boolean(resolveTarget)} onClose={() => setResolveTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Resolve exception</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mb: 1.5 }}>
            {resolveTarget?.title}
          </Typography>
          <TextField
            label="Resolution note"
            fullWidth
            multiline
            minRows={2}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: 'none' }}
            disabled={!resolveTarget || busyId === exceptionId(resolveTarget)}
            onClick={async () => {
              const target = resolveTarget;
              setResolveTarget(null);
              await patchStatus(target, 'resolved', resolutionNote);
            }}
          >
            Mark resolved
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
